import { FC, memo, useCallback, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'

import { OnePersonListItem } from '@factorialco/f0-react/dist/experimental'
import { TeamStatusActivityQuery } from 'generated/resources/graphql'
import moment from 'moment'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import buildUrl from 'modules/core/lib/buildUrl'
import { getEmployeeDisplayName } from 'modules/core/lib/employeeName'
import i18n from 'modules/core/lib/i18n'

import { getActions, getAvatarBadge, getBottomTags, getDescription, getRightTag } from './utils'

type ItemData = TeamStatusActivityQuery['timeInsights']['clockInStatusesConnection']['nodes'][0]

const Item: FC<{
  data: ItemData
  onEmailReminder: (data: { employeeId: string; employeeFullName: string }) => void
}> = ({ data, onEmailReminder }) => {
  const { employee: currentEmployee } = useCurrentSession()
  const { navigation } = useNavigation()

  const navigate = useNavigate()

  const { employee } = data

  const managerName = employee?.manager ? getEmployeeDisplayName(employee.manager) : undefined

  const info = managerName ? `${i18n.t('common.manager')}: ${managerName}` : undefined

  const handleClick = () => {
    if (employee?.id === currentEmployee?.id) {
      navigate(navigation.profile.fullPath())
    } else {
      navigate(navigation.employees.id.fullPath({ id: employee?.id ?? '' }))
    }
  }

  const avatarBadge = useMemo(() => getAvatarBadge(data.status), [data.status])

  const bottomTags = useMemo(() => getBottomTags(data), [data])

  const handleSendReminder = useCallback(() => {
    if (!employee) return

    onEmailReminder({
      employeeId: employee.id,
      employeeFullName: getEmployeeDisplayName(employee),
    })
  }, [employee, onEmailReminder])

  const handleSchedule = useCallback(() => {
    if (!employee) return

    const linkParams = {
      periodType: 'monthly',
      year: moment().year(),
      month: moment().month() + 1,
      day: moment().date(),
    }

    const scheduleLink =
      employee?.id === currentEmployee?.id
        ? navigation.clockIn.periodTypeYearMonthDay.fullPath(linkParams)
        : navigation.timeTracking.employee.periodTypeYearMonthDayEmployeeId.fullPath({
            ...linkParams,
            employeeId: employee?.id ?? '',
          })

    navigate(scheduleLink)
  }, [employee, navigate, currentEmployee, navigation])

  const actions = useMemo(
    () =>
      getActions({
        data,
        handleSchedule,
        handleSendReminder,
      }),
    [data, handleSchedule, handleSendReminder]
  )

  const description = useMemo(() => getDescription(data), [data])

  const rightTag = useMemo(() => getRightTag(data), [data])

  if (!employee) return null

  const firstName = employee.firstName
  const lastName = employee.lastName
  const avatarUrl = employee.avatar?.url ? buildUrl(employee.avatar.url) : undefined

  const person = {
    firstName,
    lastName,
    avatarUrl,
    avatarBadge,
  }

  return (
    <OnePersonListItem
      person={person}
      info={info}
      description={description}
      bottomTags={bottomTags}
      actions={actions}
      rightTag={rightTag}
      onClick={handleClick}
      withPointerCursor
    />
  )
}

export default memo(Item)
